import type { FC } from 'react';

import { absoluteUrl } from 'src/utils/storefrontUrl';
import { APP_TITLE, formatDocumentTitle } from 'src/utils/documentTitle';

export interface DocumentHeadProps {
  /**
   * What names the tab (BUG-15): the screen's parts, joined as `<part> · <part> · GAC`. Empty and
   * missing parts are dropped, so a screen can pass `[car?.name, 'Rental']` while the car is loading.
   */
  title: string | (string | null | undefined)[];
  /** The title is complete as given — no `· GAC` after it. */
  bare?: boolean;
  /** One or two sentences about the page, for a search result and a link preview. */
  description?: string;
  /**
   * The page's one address — a path with its query, as the catalog builds it. Search engines are
   * told to file every other spelling of the page under it, and a shared link names it.
   */
  canonical?: string;
  /** The picture of a link preview: an absolute address. */
  image?: string;
}

/**
 * What a screen says of itself in the document's `<head>`: the tab title for a person, and — on a
 * page the server renders — the description, the address and the picture a search engine and a
 * link preview read (storefront-catalog spec §4.5).
 *
 * The tags are written where the screen is, next to the data they speak of; React moves them into
 * `<head>` itself — in the server's markup and in the browser alike — and takes them away with the
 * screen. So exactly one screen names the tab at a time, and a route that renders none leaves none
 * behind. A screen behind the splash has the shell's title until it is up (`root.tsx`).
 */
export const DocumentHead: FC<DocumentHeadProps> = ({ title, bare, description, canonical, image }) => {
  const parts = Array.isArray(title) ? title : [title];
  const text = bare ? parts.join(' ') : formatDocumentTitle(...parts);
  const address = canonical ? absoluteUrl(canonical) : undefined;

  return (
    <>
      <title>{text}</title>
      {description ? <meta name="description" content={description} /> : null}
      {address ? <link rel="canonical" href={address} /> : null}
      {description || address || image ? (
        <>
          <meta property="og:site_name" content={APP_TITLE} />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={text} />
          {description ? <meta property="og:description" content={description} /> : null}
          {address ? <meta property="og:url" content={address} /> : null}
          {image ? <meta property="og:image" content={image} /> : null}
          <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
        </>
      ) : null}
    </>
  );
};
